import { Link } from "react-router-dom";
import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

export interface AIBlockContent {
  badge?: string;
  headline: string;
  body: string;
  bullets: string[];
  ctaLabel: string;
  ctaPath: string;
  image1: string;
  image2: string;
  mapUrl?: string;
}

interface AIAnswerBlockProps {
  content: AIBlockContent;
}

export default function AIAnswerBlock({ content }: AIAnswerBlockProps) {
  const { badge, headline, body, bullets, ctaLabel, ctaPath, image1, image2 } = content;
  const leftRef = useScrollRevealChildren();
  const rightRef = useScrollRevealChildren();

  return (
    <section className="bg-[#F5F0E8] py-32 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-6 xl:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 xl:gap-28 items-center">

          {/* Left Content */}
          <div ref={leftRef}>
            {badge && (
              <div className="reveal flex items-center gap-4 mb-7">
                <div className="h-px w-12 bg-[#C9A961]" />
                <span
                  className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {badge}
                </span>
              </div>
            )}
            <h2
              className="reveal delay-100 text-4xl md:text-5xl xl:text-[58px] text-[#1C1C1C] leading-tight mb-8"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, letterSpacing: "-0.01em" }}
            >
              {headline}
            </h2>
            <p
              className="reveal delay-200 text-[#555] text-[15px] leading-[2] mb-10"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
            >
              {body}
            </p>

            <ul className="space-y-3.5 mb-12">
              {bullets.map((bullet, idx) => (
                <li
                  key={idx}
                  className={`reveal delay-${Math.min(idx * 80 + 300, 700)} flex items-start gap-4`}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#C9A961]">
                    <i className="ri-check-line text-white text-[10px]" />
                  </div>
                  <span
                    className="text-[#444] text-sm leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {bullet}
                  </span>
                </li>
              ))}
            </ul>

            <div className="reveal delay-600 flex flex-wrap gap-4">
              <Link
                to={ctaPath}
                className="inline-flex items-center gap-3 bg-[#1C1C1C] text-white text-[10px] tracking-[0.22em] uppercase font-semibold px-10 py-4 hover:bg-[#C9A961] transition-all duration-400 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {ctaLabel}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </Link>
            </div>
          </div>

          {/* Right: Overlapping image collage OR map */}
          <div ref={rightRef} className="reveal-right relative h-[600px]">
            {content.mapUrl ? (
              <>
                {/* Google Maps embed */}
                <div className="absolute inset-0 border border-[#E8E2D9] overflow-hidden">
                  <iframe
                    src={content.mapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Kitchen Renovations Perfected - Boca Raton Location"
                  />
                </div>
                {/* Corner accent */}
                <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#C9A961] z-10" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#C9A961]/30 z-10" />
              </>
            ) : (
              <>
                {/* Decorative vertical gold line */}
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#C9A961]/40 via-[#C9A961]/20 to-transparent z-0" />

                {/* Large image */}
                <div className="absolute top-0 left-8 right-0 h-[72%] overflow-hidden">
                  <img
                    src={image1}
                    alt="Premium kitchen renovation"
                    className="w-full h-full object-cover object-top hover:scale-[1.04] transition-transform duration-1000"
                  />
                  {/* Subtle corner accent */}
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#C9A961]" />
                </div>

                {/* Smaller overlapping image */}
                <div className="absolute bottom-0 left-0 w-[58%] h-[48%] overflow-hidden border-4 border-[#F5F0E8] z-10">
                  <img
                    src={image2}
                    alt="KRP design craftsmanship"
                    className="w-full h-full object-cover object-top hover:scale-[1.04] transition-transform duration-1000"
                  />
                </div>

                {/* Gold rating badge */}
                <div className="absolute bottom-[32%] right-2 z-20 bg-[#C9A961] text-white px-5 py-4 text-center">
                  <div
                    className="text-[36px] font-bold leading-none mb-1"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    4.9
                  </div>
                  <div className="flex justify-center gap-0.5 mb-1">
                    {[1,2,3,4,5].map((s) => (
                      <i key={s} className="ri-star-fill text-white/80 text-[9px]" />
                    ))}
                  </div>
                  <div
                    className="text-[8px] tracking-[0.2em] uppercase text-white/75"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Star Rated
                  </div>
                </div>

                {/* Corner accent bottom right */}
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#C9A961]/30 z-0" />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
